import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GENRES } from "@/lib/genres";
import { MOODS } from "@/lib/moods";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import CreateProgress from "@/components/CreateProgress";
import { GenrePicker } from "@/components/create/GenrePicker";
import { LyricsStep } from "@/components/create/LyricsStep";
import { CoverStep } from "@/components/create/CoverStep";
import { RegenLyricsModal } from "@/components/create/RegenLyricsModal";
import { CreateTips } from "@/components/create/CreateTips";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";

const LOADING_MESSAGES = [
  "Escribiendo letra...",
  "Buscando rimas perfectas...",
  "Afinando el coro...",
  "Tu historia se está convirtiendo en canción..."
];

const COVER_LOADING = [
  "Procesando tu foto...",
  "Afinando guitarras...",
  "Grabando voces...",
  "Mezclando instrumentos...",
];

export default function CreateSong() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialSongId = params.get("song_id");

  const [step, setStep] = useState(1);
  const [genre, setGenre] = useState("");
  const [story, setStory] = useState("");
  const [artistName, setArtistName] = useState(user?.name || "");
  const [title, setTitle] = useState("");
  const [autoTitle, setAutoTitle] = useState(false);
  const [styleRef, setStyleRef] = useState("");
  const [musicStyleDesc, setMusicStyleDesc] = useState("");
  const [voiceArrangement, setVoiceArrangement] = useState("");
  const [mood, setMood] = useState("");
  const [songId, setSongId] = useState(initialSongId || "");
  const [lyrics, setLyrics] = useState("");
  // Original AI-generated lyrics — used to compute how many lines the user has
  // edited manually, and to power the "Restore AI version" button. Manual edits
  // are FREE (zero tokens), so we encourage them visually.
  const [originalLyrics, setOriginalLyrics] = useState("");
  const [finalTitle, setFinalTitle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const [coverPreview, setCoverPreview] = useState("");
  const [albumName, setAlbumName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [coverMsg, setCoverMsg] = useState(COVER_LOADING[0]);

  // Regen state
  const [regenInfo, setRegenInfo] = useState(null); // { kinds: { lyrics: {free_remaining, ...} }, regen_credits }
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);

  // Lyrics review/edit mode. Default = review (read-only). Toggled by "Modificar letra".
  const [lyricsEditMode, setLyricsEditMode] = useState(false);

  // Load regen info whenever we have a songId and are in step 2
  useEffect(() => {
    if (!songId || step !== 2) return;
    api.get(`/songs/${songId}/regen-info`).then(r => setRegenInfo(r.data)).catch(() => {});
  }, [songId, step]);

  // Credit gate: redirect to /payment if user has no credits and no in-progress song.
  // IMPORTANT: once we've already generated a song in this session (songId is set),
  // do NOT redirect — the credit was just consumed and the user must stay on the
  // page to approve/edit lyrics and continue the creation flow.
  useEffect(() => {
    if (!user) return;
    if (initialSongId) return; // resuming an in-progress song is allowed
    if (songId) return; // song already created in this session — stay to approve lyrics
    if ((user.song_credits || 0) <= 0) {
      toast.error("Necesitas un crédito para crear una canción");
      nav("/payment");
    }
  }, [user, initialSongId, songId, nav]);

  // Rotate Claude loading messages
  useEffect(() => {
    if (!generating) return;
    let idx = 0;
    const t = setInterval(() => {
      idx = (idx + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[idx]);
    }, 2200);
    return () => clearInterval(t);
  }, [generating]);

  // Rotate cover/audio loading messages
  useEffect(() => {
    if (!uploading) return;
    let idx = 0;
    const t = setInterval(() => {
      idx = (idx + 1) % COVER_LOADING.length;
      setCoverMsg(COVER_LOADING[idx]);
    }, 1800);
    return () => clearInterval(t);
  }, [uploading]);

  // Resume in-progress song if song_id is in URL
  useEffect(() => {
    if (!initialSongId) return;
    api.get(`/songs/${initialSongId}`).then(r => {
      const s = r.data;
      setGenre(s.genre);
      setArtistName(s.artist_name);
      setLyrics(s.lyrics);
      setOriginalLyrics(s.lyrics);
      setFinalTitle(s.title);
      if (s.audio_url || ["generating_audio", "audio_failed"].includes(s.status)) nav(`/song/${s.id}`);
      else if (s.cover_image_b64) {
        setCoverPreview(`data:image/jpeg;base64,${s.cover_image_b64}`);
        setStep(3);
      } else if (s.lyrics_approved) {
        setStep(3);
      } else {
        setStep(2);
      }
    }).catch(() => {});
  }, [initialSongId, nav]);

  const generateLyrics = async () => {
    // Build a list of all missing fields → show a single LARGE persistent toast
    const missing = [];
    if (!genre) missing.push("Selecciona un género musical");
    if (story.trim().length < 30) missing.push(`${genre === "instrumental" ? "Describe la emoción que quieres provocar" : "Cuéntanos tu historia"} (mínimo 30 caracteres)`);
    if (genre !== "instrumental" && !artistName.trim()) missing.push("Escribe tu nombre de artista o pseudónimo");
    if (!autoTitle && !title.trim()) missing.push("Escribe el título o activa 'Que la IA lo decida'");
    if (missing.length) {
      toast.error(
        `Falta completar:\n• ${missing.join("\n• ")}`,
        { duration: 8000, style: { fontSize: "15px", lineHeight: "1.5", padding: "16px", whiteSpace: "pre-line", maxWidth: "92vw" } }
      );
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/songs/create", {
        genre,
        artist_name: genre === "instrumental" ? (artistName.trim() || "Pieza instrumental") : artistName.trim(),
        title: autoTitle ? null : title.trim(),
        story: story.trim(),
        auto_title: autoTitle,
        style_reference: styleRef.trim() || null,
        music_style_description: musicStyleDesc.trim() || null,
        voice_arrangement: voiceArrangement || null,
        mood: mood || null,
      });
      setSongId(data.id);
      setLyrics(data.lyrics);
      setOriginalLyrics(data.lyrics);
      setFinalTitle(data.title);
      setLyricsEditMode(false); // Open in review mode by default
      await refreshUser();
      setStep(2);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Sin créditos. Te llevo al pago…");
        nav("/payment");
        return;
      }
      const detail = err?.response?.data?.detail || "Hubo un problema generando la letra. Intenta de nuevo en un momento.";
      // Make the error toast bigger and longer-lived for critical failures
      toast.error(detail, {
        duration: 10000,
        style: { fontSize: "15px", lineHeight: "1.4", padding: "16px", maxWidth: "92vw" }
      });
    } finally { setGenerating(false); }
  };

  const approveLyrics = async () => {
    try {
      await api.post("/songs/approve-lyrics", { song_id: songId, final_lyrics: lyrics });
      toast.success("Letra aprobada");
      setStep(3);
    } catch { toast.error("Error aprobando la letra"); }
  };

  const regenerateLyrics = async () => {
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/songs/${songId}/regenerate-lyrics`, {
        extra_instructions: regenInstructions.trim() || null,
      });
      setLyrics(data.lyrics);
      setOriginalLyrics(data.lyrics);
      setFinalTitle(data.title);
      setShowRegenModal(false);
      setRegenInstructions("");
      // Refresh regen info & user wallet
      const fresh = await api.get(`/songs/${songId}/regen-info`);
      setRegenInfo(fresh.data);
      await refreshUser();
      toast.success(data.free ? "¡Nueva letra lista! (cambio gratis usado)" : "¡Nueva letra lista! (-1 cambio extra)");
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Sin cambios extras. Te llevo a comprarlos…");
        nav("/cambios-extra");
        return;
      }
      toast.error(err?.response?.data?.detail || "Error regenerando letra");
    } finally { setRegenLoading(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Máximo 8MB");
    setUploading(true);
    const fd = new FormData();
    fd.append("song_id", songId);
    fd.append("file", file);
    if (albumName.trim()) fd.append("album_name", albumName.trim());
    try {
      const { data } = await api.post("/songs/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCoverPreview(data.cover_data_url);
      // give the rotating messages a beat so the user perceives "audio rendering"
      setTimeout(() => {
        toast.success(data.audio_generating
          ? "¡Portada lista! Tu canción se está componiendo…"
          : "¡Canción lista!");
        nav(`/song/${songId}`);
      }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo la foto");
      setUploading(false);
    }
  };

  const noPhotoCover = async () => {
    setUploading(true);
    toast.info("Generando portada con IA (10-15 seg)…", { duration: 8000 });
    const fd = new FormData();
    fd.append("song_id", songId);
    if (albumName.trim()) fd.append("album_name", albumName.trim());
    try {
      const { data } = await api.post("/songs/no-photo-cover", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setCoverPreview(data.cover_data_url);
      setTimeout(() => {
        toast.success(data.audio_generating
          ? "¡Portada IA lista! Tu canción se está componiendo…"
          : "¡Canción lista!");
        nav(`/song/${songId}`);
      }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generando portada");
      setUploading(false);
    }
  };

  const charCount = story.length;
  const overLimit = charCount > 3000;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Visual progress bar (5 steps total: 3 here + 2 in SongDetail/Listen) */}
        <CreateProgress
          currentStep={step}
          reachableMax={step}
          onStepClick={(k) => {
            if (k < step) setStep(k);
          }}
        />

        {/* STEP 1 */}
        {step === 1 && !generating && (
          <div data-testid="step-1">
            <CreateTips />

            <p className="text-sm uppercase tracking-widest text-[#FFB800] mb-2">Paso 1 de 3</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Selecciona un género</h1>
            <p className="text-white/60 mb-6">
              {genre
                ? "Listo. Ahora completa tu información para que la IA componga tu canción."
                : "Empieza eligiendo el estilo musical. Después te pediremos el resto."}
            </p>

            <div className="mb-6">
              <label className="text-xs uppercase tracking-widest text-white/60 mb-3 block">Género musical</label>
              <GenrePicker genre={genre} onSelect={setGenre} />
            </div>

            {/* Everything below only appears once a genre is selected (cleaner UX) */}
            {genre && (<>

            {genre !== "instrumental" && (
              <div className="mb-6">
                <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Nombre del artista</label>
                <input
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  placeholder="Tu nombre o seudónimo"
                  className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors"
                  data-testid="input-artist-name"
                />
              </div>
            )}

            <div className="mb-4">
              <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block flex items-center justify-between">
                Título <span className="normal-case tracking-normal text-white/40 text-[10px]">opcional si IA elige</span>
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Mi canción"
                disabled={autoTitle}
                className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors disabled:opacity-40"
                data-testid="input-title"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 mb-6 cursor-pointer">
              <input type="checkbox" checked={autoTitle} onChange={(e) => setAutoTitle(e.target.checked)} className="w-4 h-4 accent-[#FFB800]" data-testid="toggle-auto-title" />
              Que la IA elija el título
            </label>

            {/* Style reference - dynamic per genre. Hidden for instrumental (no artist reference). */}
            {genre && genre !== "instrumental" && (() => {
              const g = GENRES.find(x => x.key === genre);
              const selected = styleRef.split(",").map(s => s.trim()).filter(Boolean);
              const isSelected = (a) => selected.some(s => s.toLowerCase() === a.toLowerCase());
              const toggleArtist = (a) => {
                if (isSelected(a)) {
                  setStyleRef(selected.filter(s => s.toLowerCase() !== a.toLowerCase()).join(", "));
                } else {
                  setStyleRef(selected.length ? `${selected.join(", ")}, ${a}` : a);
                }
              };
              const focusInput = () => {
                const el = document.querySelector('[data-testid="input-style-reference"]');
                if (el) el.focus();
              };
              return (
                <div className="mb-6" data-testid="style-ref-block">
                  <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block flex items-center justify-between">
                    <span>{g?.key === "himnos" ? "¿Qué estilo de himno prefieres?" : "¿En el estilo de qué artista?"}</span>
                    <span className="normal-case tracking-normal text-white/40 text-[10px]">opcional · recomendado</span>
                  </label>
                  <input
                    value={styleRef}
                    onChange={(e) => setStyleRef(e.target.value)}
                    placeholder={`Ej: ${g?.style_examples || ""}`}
                    className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors placeholder-white/30"
                    data-testid="input-style-reference"
                  />

                  {/* Quick-pick chips */}
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mt-3 mb-2">Toca para agregar</p>
                  <div className="flex flex-wrap gap-2" data-testid="style-chips">
                    {(g?.quick_artists || []).map((a) => {
                      const on = isSelected(a);
                      return (
                        <button
                          key={a}
                          type="button"
                          onClick={() => toggleArtist(a)}
                          data-testid={`chip-${a.replace(/\s+/g, '-').toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all active:scale-95 ${
                            on
                              ? 'bg-[#FFB800] border-[#FFB800] text-black'
                              : 'bg-white/5 border-white/10 text-white/80 hover:border-white/30 hover:bg-white/10'
                          }`}
                        >
                          {on ? '✓ ' : ''}{a}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={focusInput}
                      data-testid="chip-otros"
                      className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-white/20 text-white/60 hover:border-[#B4F34A] hover:text-[#B4F34A] transition-all"
                    >
                      + Otros (escribir)
                    </button>
                  </div>

                  <p className="text-xs text-white/40 mt-3 leading-relaxed">
                    💡 La IA imitará el tono lírico y vocabulario del artista que elijas. En gustos se rompen géneros: puedes mezclar varios o escribir uno que no esté en la lista.
                  </p>
                </div>
              );
            })()}

            {/* Mood selector */}
            <div className="mb-6" data-testid="mood-block">
              <label className="text-xs uppercase tracking-widest text-white/60 mb-3 block flex items-center justify-between">
                <span>¿Qué tono quieres que tenga?</span>
                <span className="normal-case tracking-normal text-white/40 text-[10px]">opcional</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="mood-chips">
                {MOODS.map((m) => {
                  const on = mood === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setMood(on ? "" : m.key)}
                      data-testid={`mood-${m.key}`}
                      className={`px-3 py-3 rounded-xl text-sm font-medium border transition-all active:scale-95 text-left ${
                        on
                          ? 'bg-[#FFB800] border-[#FFB800] text-black'
                          : 'bg-[#121212] border-white/10 text-white/80 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <span className="text-lg block leading-none mb-1">{m.emoji}</span>
                      <span className="font-display font-bold block leading-tight">{m.label}</span>
                      <span className={`block text-[10px] mt-1 leading-tight ${on ? 'text-black/70' : 'text-white/40'}`}>
                        {m.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Music style free-text (sound direction) */}
            <div className="mb-6" data-testid="music-style-block">
              <label htmlFor="music-style-input" className="text-xs uppercase tracking-widest text-white/60 mb-2 block flex items-center justify-between">
                <span>¿Qué tipo de música quieres que lleve tu canción?</span>
                <span className="normal-case tracking-normal text-white/40 text-[10px]">opcional · escribe libre</span>
              </label>
              <input
                id="music-style-input"
                value={musicStyleDesc}
                onChange={(e) => setMusicStyleDesc(e.target.value.slice(0, 200))}
                placeholder="Ej: Balada con banda tipo Banda MS · Reggaeton lento con guitarra acústica · Cumbia con violines"
                className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#B4F34A] focus:outline-none transition-colors placeholder-white/30 text-sm"
                data-testid="input-music-style"
              />
              <p className="text-xs text-white/40 mt-2 leading-relaxed">
                🎼 Describe libremente la <span className="text-white">sonoridad</span>: fusión de géneros, instrumentos clave, tempo o ambiente. La IA usará esto para construir tanto la letra como el audio.
              </p>
              <div className="text-[10px] text-white/30 text-right mt-1">{musicStyleDesc.length}/200</div>
            </div>

            {/* Voice arrangement (gender + solo/duet) — not applicable for instrumental */}
            {genre !== "instrumental" && (
            <div className="mb-6" data-testid="voice-arrangement-block">
              <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block flex items-center justify-between">
                <span>Voz de la canción</span>
                <span className="normal-case tracking-normal text-white/40 text-[10px]">opcional</span>
              </label>
              {/* Grouped list: 4 male textures + 4 female textures + 3 duets */}
              <div className="space-y-4">
                {[
                  {
                    section: "Voces masculinas",
                    accent: "#4BC8DC",
                    options: [
                      { key: "male_solo_young",    label: "Joven relajada",   sub: "Con swag, moderna" },
                      { key: "male_solo_mature",   label: "Madura sentida",   sub: "Barítono cálido" },
                      { key: "male_solo_powerful", label: "Potente carácter", sub: "Presencia rasposa" },
                      { key: "male_solo_smooth",   label: "Suave romántica",  sub: "Tenor cercano" },
                    ],
                  },
                  {
                    section: "Voces femeninas",
                    accent: "#FF9CC8",
                    options: [
                      { key: "female_solo_sweet",    label: "Dulce y clara",   sub: "Cristalina, pop" },
                      { key: "female_solo_powerful", label: "Potente fuerza",  sub: "Belt, dramática" },
                      { key: "female_solo_sultry",   label: "Sensual actitud", sub: "Grave, coqueta" },
                      { key: "female_solo_elegant",  label: "Elegante clase",  sub: "Madura, refinada" },
                    ],
                  },
                  {
                    section: "Duetos",
                    accent: "#B4F34A",
                    options: [
                      { key: "duet_mf", label: "Hombre + Mujer", sub: "Alternado, contraste" },
                      { key: "duet_mm", label: "Hombre + Hombre", sub: "Dos voces masculinas" },
                      { key: "duet_ff", label: "Mujer + Mujer",   sub: "Dos voces femeninas" },
                    ],
                  },
                ].map(({ section, accent, options }) => (
                  <div key={section}>
                    <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: `${accent}` }}>{section}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {options.map((v) => {
                        const active = voiceArrangement === v.key;
                        return (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => setVoiceArrangement(active ? "" : v.key)}
                            data-testid={`voice-${v.key}`}
                            className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                              active
                                ? "bg-white/10 text-white"
                                : "bg-[#121212] border-white/10 text-white/70 hover:border-white/25"
                            }`}
                            style={active ? { borderColor: accent, boxShadow: `0 0 0 1px ${accent}` } : {}}
                          >
                            <div className="text-[13px] font-bold leading-tight">{v.label}</div>
                            <div className="text-[10px] text-white/50 mt-0.5 leading-tight">{v.sub}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-white/40 mt-3 leading-relaxed">
                🎵 Elige la textura de voz que mejor va con tu historia. Cada opción tiene un carácter sonoro diferente.
              </p>
            </div>
            )}

            <div className="mb-8">
              <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                {genre === "instrumental" ? "¿Qué quieres que sienta el oyente?" : "Tu historia"}
              </label>
              <textarea
                rows={8}
                value={story}
                onChange={(e) => setStory(e.target.value)}
                placeholder={
                  genre === "instrumental"
                    ? "Ej: Quiero que se sienta nostálgico, recordando un atardecer en la playa. Que el oyente cierre los ojos y viaje a ese momento de paz mezclado con un poco de melancolía…"
                    : "Soy de Sinaloa, mi abuelo era ranchero y trabajaba la tierra. Crecí escuchando corridos en su troca…"
                }
                className={`w-full px-4 py-3 rounded-xl bg-[#121212] border ${overLimit ? 'border-[#FF4B00]' : 'border-white/10 focus:border-[#FFB800]'} focus:outline-none transition-colors resize-none leading-relaxed`}
                data-testid="input-story"
              />
              <div className="flex justify-between text-xs text-white/40 mt-2">
                <span>
                  {genre === "instrumental"
                    ? "Describe la emoción y la imagen mental que quieres provocar — la IA convertirá eso en música pura, sin voz."
                    : "Cuéntanos todo lo que quieras transformar en canción."}
                </span>
                <span className={overLimit ? 'text-[#FF4B00]' : ''} data-testid="char-counter">{charCount} / 3000</span>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-3 px-4 py-3 rounded-xl bg-[#FFB800]/5 border border-[#FFB800]/20">
              <Sparkle weight="fill" size={18} className="text-[#FFB800] shrink-0" />
              <p className="text-xs text-white/70">
                Generar la letra consume <span className="text-white font-semibold">1 crédito</span>. Tienes <span className="text-[#FFB800] font-bold" data-testid="credits-balance">{user.song_credits}</span> disponible{user.song_credits === 1 ? '' : 's'}.
              </p>
            </div>

            {/* Commercial use note: the real authorization (with bank data) happens later
                in SongDetail. Here we just inform the user this is OPTIONAL and explain the upside. */}
            <div className="mb-4 px-4 py-3 rounded-xl bg-[#B4F34A]/5 border border-[#B4F34A]/20" data-testid="commercial-use-note">
              <p className="text-xs text-white/80 leading-relaxed">
                <span className="font-semibold text-[#B4F34A]">¿Quieres ganar regalías?</span> Cuando tu canción esté lista, podrás autorizar su uso comercial y participar para entrar al repertorio que vendemos a restaurantes y bares. <span className="text-white/50">Lo decides después, no ahora.</span>
              </p>
            </div>

            <button
              onClick={generateLyrics}
              disabled={generating || overLimit}
              className="w-full py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-50 text-black font-semibold transition-all active:scale-95 flex items-center justify-center gap-2"
              data-testid="btn-generate-lyrics"
            >
              Generar letra <ArrowRight weight="bold" size={18} />
            </button>
            </>)}
          </div>
        )}

        {generating && (
          <div className="py-20 text-center" data-testid="loading-lyrics">
            <div className="soundwave mx-auto mb-6">
              <span></span><span></span><span></span><span></span><span></span><span></span><span></span>
            </div>
            <p className="font-display text-2xl sm:text-3xl font-bold pulse-title">{loadingMsg}</p>
            <p className="text-white/40 text-sm mt-3">Esto toma unos segundos…</p>
          </div>
        )}

        {/* STEP 2 - LYRICS */}
        {step === 2 && !generating && (
          <LyricsStep
            lyrics={lyrics}
            setLyrics={setLyrics}
            originalLyrics={originalLyrics}
            finalTitle={finalTitle}
            setFinalTitle={setFinalTitle}
            editMode={lyricsEditMode}
            setEditMode={setLyricsEditMode}
            onApprove={approveLyrics}
            onBack={() => setStep(1)}
            onOpenRegen={() => setShowRegenModal(true)}
            regenInfo={regenInfo}
          />
        )}

        {/* STEP 3 - PHOTO + final */}
        {step === 3 && !uploading && (
          <CoverStep
            albumName={albumName}
            setAlbumName={setAlbumName}
            coverPreview={coverPreview}
            uploading={uploading}
            onFile={onFile}
            onNoPhoto={noPhotoCover}
            onBack={() => setStep(2)}
          />
        )}

        {uploading && (
          <div className="py-20 text-center" data-testid="loading-cover">
            <div className="soundwave mx-auto mb-6">
              <span></span><span></span><span></span><span></span><span></span><span></span><span></span>
            </div>
            <p className="font-display text-2xl sm:text-3xl font-bold pulse-title">{coverMsg}</p>
            <p className="text-white/40 text-sm mt-3">Estamos terminando tu canción…</p>
          </div>
        )}
      </div>

      <RegenLyricsModal
        open={showRegenModal}
        regenInfo={regenInfo}
        instructions={regenInstructions}
        setInstructions={setRegenInstructions}
        loading={regenLoading}
        onCancel={() => { setShowRegenModal(false); setRegenInstructions(""); }}
        onConfirm={regenerateLyrics}
      />
    </div>
  );
}
